import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  forwardRef,
  ChangeDetectionStrategy,
  Input,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  Validator,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { skip } from 'rxjs/operators';

export interface ListFieldRow {
  year: number;
  dateFrom: Date | null;
  dateTill: Date | null;
  isDayoff: boolean;
}

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'cs-list-fields-form',
  templateUrl: './list-fields-form.component.html',
  styleUrls: ['./list-fields-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ListFieldsFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ListFieldsFormComponent),
      multi: true,
    },
  ],
})
export class ListFieldsFormComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {
  rowsFormArray: UntypedFormArray;
  yearOptions: number[] = [];

  /**
   * Column visibility map for controlling which columns are shown.
   * Key: column name (e.g., 'dateTill'), Value: visibility state
   */
  @Input() columnVisibility?: Map<string, boolean>;

  /**
   * Check if a column should be visible
   */
  isColumnVisible(columnName: string): boolean {
    if (!this.columnVisibility) {
      return true; // All columns visible by default
    }
    const visibility = this.columnVisibility.get(columnName);
    return visibility !== false; // Visible unless explicitly set to false
  }

  get rows(): UntypedFormArray {
    return this.rowsFormArray;
  }

  constructor(private cdRef: ChangeDetectorRef) {
    this.rowsFormArray = new UntypedFormArray([]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columnVisibility']) {
      // Re-emit value when column visibility changes so hidden columns are excluded from data
      if (this.initialized && !changes['columnVisibility'].firstChange) {
        this.emitValue();
      }
    }
  }

  ngOnDestroy(): void {
  }

  ngOnInit(): void {
    this.initializeYearOptions();
    // Note: Don't initialize default rows here - writeValue will be called
    // by Angular forms and will either set value or trigger initializeDefaultRows
				this.setupValueChangesListener();
  }

  addRow(initial?: Partial<ListFieldRow>): void {
    const availableYears = this.getAvailableYears();
    if (availableYears.length === 0) {
      return; // No more years available
    }
    // Pick the first available year (sorted ascending, so earliest unused year)
    const newYear = availableYears[0];
    this.rowsFormArray.push(this.createRow(newYear, initial));
    this.rowsFormArray.markAsDirty();
  }

  /** Returns years from yearOptions that are not yet used in any row */
  getAvailableYears(): number[] {
    const usedYears = (this.rowsFormArray.value as ListFieldRow[])
      .map(row => Number(row.year))
      .filter(year => year != null && !isNaN(year));
    return this.yearOptions.filter(year => !usedYears.includes(year));
  }

  /** Check if we can add more rows (there are unused years available) */
  canAddRow(): boolean {
    return this.getAvailableYears().length > 0;
  }

  /** Returns years that are already used in rows */
  getUsedYears(): number[] {
    return (this.rowsFormArray.value as ListFieldRow[])
      .map(row => Number(row.year))
      .filter(year => year != null && !isNaN(year));
  }

  /** Check if a year is used in another row (but not the current one) */
  isYearDisabled(year: number, currentIndex: number): boolean {
    const usedYears = this.getUsedYears();
    const currentYear = this.rowsFormArray.at(currentIndex)?.get('year')?.value;
    const currentYearNum = currentYear != null ? Number(currentYear) : null;
    // Disable if year is used AND it's not the current row's year
    return usedYears.includes(year) && year !== currentYearNum;
  }

  /** Handle year selection change - set dateFrom and dateTill for the selected year */
  onYearChange(index: number, yearValue: any): void {
    if (yearValue == null || yearValue === '') {
      return;
    }
    const year = Number(yearValue);
    if (isNaN(year)) {
      return;
    }
    const rowGroup = this.rowsFormArray.at(index) as UntypedFormGroup;
    if (rowGroup) {
      const dateFrom = new Date(year, 0, 1); // January 1st
      const dateTill = new Date(year, 11, 31); // December 31st
      rowGroup.patchValue({ dateFrom, dateTill });
    }
  }


  removeRow(index: number): void {
    this.rowsFormArray.removeAt(index);
    this.rowsFormArray.markAsDirty();
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ControlValueAccessor implementation
  writeValue(value: ListFieldRow[] | null): void {
    // Check if value is empty or contains only empty/null rows
    const hasValidData = value && value.length > 0 && value.some(row => 
      row.year != null || row.dateFrom != null || row.dateTill != null
    );
    
    if (!hasValidData) {
      // Only initialize default rows if no rows exist yet
      if (this.rowsFormArray.length === 0) {
        this.initializeDefaultRows();
      }
    } else {
      this.rowsFormArray.clear();
      value.forEach((row) => {
        this.rowsFormArray.push(this.createRow(row.year, row));
      });
    }
    this.rowsFormArray.markAsUntouched();
    this.rowsFormArray.markAsPristine();
    this.initialized = true;
  }

  registerOnChange(fn: (value: ListFieldRow[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.rowsFormArray.disable() : this.rowsFormArray.enable();
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};
    const rows = this.rowsFormArray.value as ListFieldRow[];

    rows.forEach((row, index) => {
      const rowErrors: any = {};

      // Required validations - skip validation for hidden columns
      if (this.isColumnVisible('year') && row.year == null) {
        rowErrors.yearRequired = true;
      }

      if (this.isColumnVisible('dateFrom') && row.dateFrom == null) {
        rowErrors.dateFromRequired = true;
      }

      if (this.isColumnVisible('dateTill') && row.dateTill == null) {
        rowErrors.dateTillRequired = true;
      }

      // Cross-field validation: dateFrom must be before dateTill (only if both columns are visible)
      if (
        this.isColumnVisible('dateFrom') &&
        this.isColumnVisible('dateTill') &&
        row.dateFrom != null &&
        row.dateTill != null &&
        row.dateFrom >= row.dateTill
      ) {
        rowErrors.dateRangeInvalid = true;
      }

      // Year range validation (should be within the dropdown options) - only if year column is visible
      const yearNum = Number(row.year);
      if (this.isColumnVisible('year') && row.year != null && !isNaN(yearNum) && !this.yearOptions.includes(yearNum)) {
        rowErrors.yearOutOfRange = true;
      }

      if (Object.keys(rowErrors).length > 0) {
        if (!errors['rows']) {
          errors['rows'] = {};
        }
        errors['rows'][index] = rowErrors;
      }
    });

    return Object.keys(errors).length > 0 ? errors : null;
  }

  getRowErrors(index: number): ValidationErrors | null {
    const errors = this.validate(this.rowsFormArray);
    if (errors && errors['rows'] && errors['rows'][index]) {
      return errors['rows'][index];
    }
    return null;
  }

  /** Check if a row should show validation errors (only when touched/dirty) */
  shouldShowRowErrors(index: number): boolean {
    const rowGroup = this.rowsFormArray.at(index) as UntypedFormGroup;
    return rowGroup && (rowGroup.touched || rowGroup.dirty);
  }
  private initialized = false;
  private onChange: (value: ListFieldRow[]) => void = () => {};
  private onTouched: () => void = () => {};

  private initializeYearOptions(): void {
    const currentYear = new Date().getFullYear();
    this.yearOptions = [];
    for (let i = currentYear - 3; i <= currentYear + 3; i++) {
      this.yearOptions.push(i);
    }
  }

  private initializeDefaultRows(): void {
    const currentYear = new Date().getFullYear();
    this.rowsFormArray.clear();

    for (let i = 3; i >= 0; i--) {
      const year = currentYear - i;
      this.rowsFormArray.push(this.createRow(year));
    }
  }

  private createRow(year?: number, initial?: Partial<ListFieldRow>): UntypedFormGroup {
    const rowData = initial || {};
    const rowYear = year ?? rowData.year ?? null;
    // Set default dates based on year if not provided
    const defaultDateFrom = rowYear != null ? new Date(rowYear, 0, 1) : null;
    const defaultDateTill = rowYear != null ? new Date(rowYear, 11, 31) : null;
    
    // Create row group without validators first
    const rowGroup = new UntypedFormGroup({
      year: new UntypedFormControl(rowYear),
      dateFrom: new UntypedFormControl(rowData.dateFrom ?? defaultDateFrom),
      dateTill: new UntypedFormControl(rowData.dateTill ?? defaultDateTill),
      isDayoff: new UntypedFormControl(rowData.isDayoff ?? false),
    });
    
    // Now add validators that reference the rowGroup
    rowGroup.get('dateFrom')?.setValidators(this.createDateRangeValidator(rowGroup, 'year'));
    rowGroup.get('dateTill')?.setValidators(this.createDateRangeValidator(rowGroup, 'year'));
    
    // Watch for dateFrom changes to sync year and validate dateTill (skip first emission)
    const subscription = rowGroup.get('dateFrom')?.valueChanges
      .pipe(skip(1))
      .subscribe(() => {
        this.syncYearFromDateFrom(rowGroup);
      });
    
    return rowGroup;
  }

  private createDateRangeValidator(rowGroup: UntypedFormGroup, yearField: string) {
    return (control: AbstractControl) => {
      const date = control.value;
      if (!date || !(date instanceof Date)) {
        return null;
      }
      const year = rowGroup.get(yearField)?.value;
      if (year == null) {
        return null;
      }
      const yearNum = Number(year);
      const dateYear = date.getFullYear();
      
      if (dateYear !== yearNum) {
        return { dateOutOfRange: true };
      }
      return null;
    };
  }

  private syncYearFromDateFrom(rowGroup: UntypedFormGroup): void {
    const dateFromValue = rowGroup.get('dateFrom')?.value;
    const yearValue = rowGroup.get('year')?.value;
    
    if (dateFromValue instanceof Date && yearValue != null) {
      const yearNum = Number(yearValue);
      const dateFromYear = dateFromValue.getFullYear();
      
      // If dateFrom is outside the selected year, constrain it to the year, keeping month/day
      if (dateFromYear !== yearNum) {
        const constrainedDate = new Date(yearNum, dateFromValue.getMonth(), dateFromValue.getDate());
        rowGroup.get('dateFrom')?.setValue(constrainedDate, { emitEvent: false });
      } else {
        // Update the year field from dateFrom when within range
        rowGroup.get('year')?.setValue(dateFromYear, { emitEvent: false });
      }
      
      // Set dateTill to dateFrom + 1 day
      const nextDay = new Date(dateFromValue);
      nextDay.setDate(nextDay.getDate() + 1);
      rowGroup.get('dateTill')?.setValue(nextDay, { emitEvent: false });
    }
  }

  /** Handle dateFrom selection change - update dateTill to next day */
  onDateFromChange(index: number, dateValue: any): void {
    const rowGroup = this.rowsFormArray.at(index) as UntypedFormGroup;
    if (!rowGroup || dateValue == null) {
      return;
    }
    
    // Parse the date value (could be iddate format yyyymmdd or ISO string)
    let dateFrom: Date | null = null;
    if (typeof dateValue === 'number') {
      const str = dateValue.toString();
      if (str.length === 8) {
        const year = parseInt(str.substring(0, 4), 10);
        const month = parseInt(str.substring(4, 6), 10) - 1;
        const day = parseInt(str.substring(6, 8), 10);
        dateFrom = new Date(year, month, day);
      }
    } else if (typeof dateValue === 'string') {
      dateFrom = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      dateFrom = dateValue;
    }
    
    if (dateFrom && !isNaN(dateFrom.getTime())) {
      // Set dateTill to dateFrom + 1 day
      const nextDay = new Date(dateFrom);
      nextDay.setDate(nextDay.getDate() + 1);
      rowGroup.get('dateTill')?.setValue(nextDay);
      
      // Also update year if needed
      const year = dateFrom.getFullYear();
      rowGroup.get('year')?.setValue(year, { emitEvent: false });
    }
  }

  /** Get the year from the current row to disable dates outside that year */
  getYearForRow(index: number): number | null {
    const rowGroup = this.rowsFormArray.at(index) as UntypedFormGroup;
    if (!rowGroup) {
      return null;
    }
    const yearValue = rowGroup.get('year')?.value;
    return yearValue != null ? Number(yearValue) : null;
  }

  private setupValueChangesListener(): void {
    this.rowsFormArray.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        // Only emit when initialized and not disabled (form is visible)
        if (this.initialized && !this.rowsFormArray.disabled) {
          this.emitValue();
        }
      });
  }

  private emitValue(): void {
    const value = this.rowsFormArray.value as ListFieldRow[];
    const normalizedValue = this.normalizeValue(value);
    this.onChange(normalizedValue);
  }

  private normalizeValue(value: any[]): ListFieldRow[] {
    return value.map((row) => {
      const normalized: ListFieldRow = {
        year: row.year != null ? Number(row.year) : null,
        dateFrom:
          row.dateFrom instanceof Date
            ? row.dateFrom
            : row.dateFrom
              ? new Date(row.dateFrom)
              : null,
        dateTill:
          row.dateTill instanceof Date
            ? row.dateTill
            : row.dateTill
              ? new Date(row.dateTill)
              : null,
        isDayoff: Boolean(row.isDayoff),
      };

      // Exclude hidden columns from emitted data
      if (this.columnVisibility) {
        if (this.columnVisibility.get('dateTill') === false) {
          delete (normalized as any).dateTill;
        }
        if (this.columnVisibility.get('dateFrom') === false) {
          delete (normalized as any).dateFrom;
        }
        if (this.columnVisibility.get('year') === false) {
          delete (normalized as any).year;
        }
        if (this.columnVisibility.get('isDayoff') === false) {
          delete (normalized as any).isDayoff;
        }
      }

      return normalized;
    });
  }
}
